import { Box, Button, FormControlLabel, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatFlowCaption from 'components/@home/@settings/Tabs/ChatFlowCaption';
import ChatFlowParametersTable from 'components/@home/@settings/Tabs/ChatFlowParametersTable';
import chatFlowBuiltInTypes from 'components/@home/@settings/Tabs/chatFlowBuiltInTypes';
import chatFlowRouteFormik from 'components/@home/@settings/Tabs/chatFlowRouteFormik';
import ChatFlowSection from 'components/@home/@settings/Tabs/ChatFlowSection';
import connector from 'components/@home/@settings/Tabs/connector';
import MessageArray from 'components/@home/@settings/Tabs/MessageArray';
import withContext from 'components/@home/@settings/Tabs/withContext';
import FormikAutosuggest from 'components/formik/Autosuggest';
import FormikRadioGroup from 'components/formik/RadioGroup';
import Switch from 'components/formik/Switch';
import TextField from 'components/formik/TextField';
import { Field } from 'formik';
import orderBy from 'lodash/orderBy';
import CloseIcon from 'mdi-react/CloseIcon';
import { func, object } from 'prop-types';
import React, { useMemo } from 'react';
import { I18n } from 'utils/i18n';
import { useLazyListTeamsQuery } from 'store/app/entities/TeamsSlice';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';
import FormikChipInput from '../../../formik/ChipInput/index';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    borderLeft: `solid 1px ${theme.palette.secondary.extraLight}`,
  },
  title: {
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    color: theme.palette.primary.ultraDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  close: {
    color: theme.palette.primary.ultraDark,
  },
  body: {
    flexGrow: 1,
    overflow: 'auto',
    paddingBottom: '20px',
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  field: {
    marginTop: theme.spacing(2),
  },
  intentActions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  horizontal: {
    flexDirection: 'row',
    marginTop: '-9px',
    marginBottom: '9px',
  },
  optionHorizontal: {
    marginRight: theme.spacing(2),
    paddingRight: theme.spacing(4),
  },
  switchLabel: {
    marginLeft: 0,
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
});

const ChatFlowRoute = ({
  chatFlowIntents,
  context,
  flow,
  handleSubmit,
  setFieldValue,
  setSelectedIntent,
  setSelectedIntegration,
  values: {
    _id,
    intent,
    transition,
    transitionType,
    enableIntegration,
    integration,
    enableEmailTo,
    enableClearAllEntities,
  },
  chatFlowIntegrations,
  chatFlowEntityTypes = {},
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [triggerListTeamsQuery] = useLazyListTeamsQuery();

  if (!flow) {
    context.pop();
    return null;
  }
  if (intent._id === 'new') {
    context.push({
      type: 'intent',
      intent: 'new',
      route: _id,
      callback: setSelectedIntent,
    });
  }
  if (integration?._id === 'new') {
    context.push({
      type: 'integration',
      integration: 'new',
      route: _id,
      callback: setSelectedIntegration,
    });
  }
  const intentSuggestions = [
    { value: 'new', label: I18n.t('ChatFlowSettings.New intent') },
    ...orderBy(
      Object.values(chatFlowIntents).map(i => ({
        value: i._id,
        label: i.name,
      })),
      [i => i.label?.toLowerCase()],
    ),
  ];
  const integrationSuggestions = [
    { value: 'new', label: I18n.t('ChatFlowSettings.New integration') },
    ...(chatFlowIntegrations
      ? orderBy(
          Object.values(chatFlowIntegrations).map(i => ({
            value: i._id,
            label: i.name,
          })),
          [i => i.label?.toLowerCase()],
        )
      : []),
  ];
  const pageSuggestions = [
    { value: 'new', label: I18n.t('ChatFlowSettings.New page') },
    ...orderBy(
      flow.pages?.map(p => ({
        value: p._id,
        label: p.name,
      })) || [],
      [i => i.label?.toLowerCase()],
    ),
  ];

  const builtInEntityTypes = chatFlowBuiltInTypes;

  const entitySuggestions = [
    ...builtInEntityTypes.map(entity => ({ value: entity, label: entity })),
    ...(chatFlowEntityTypes
      ? Object.values(chatFlowEntityTypes).map(entity => ({
          value: entity.name,
          label: entity.name,
        }))
      : []),
  ].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Typography size="small" sx={styles.title}>
          {I18n.t('ChatFlowSettings.Route')}
        </Typography>
        <div>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
      <Box component="section" sx={styles.body}>
        <ChatFlowSection section="Intent" caption>
          <Box sx={styles.field}>
            <Field
              component={FormikAutosuggest}
              suggestions={intentSuggestions}
              placeholder={I18n.t('ChatFlowSettings.Any')}
              name="intent._id"
              isClearable
              clearValue={() => {
                setFieldValue('intent', {});
              }}
            />
          </Box>
          {(intent?._id || 'new') !== 'new' && (
            <Box sx={styles.intentActions}>
              <Button
                variant="text"
                color="primary"
                onClick={() => context.push({ type: 'intent', intent: intent._id })}
              >
                {I18n.t('ChatFlowSettings.Edit intent')}
              </Button>
            </Box>
          )}
        </ChatFlowSection>
        <ChatFlowSection section="Condition" caption>
          <Field
            sx={styles.field}
            component={TextField}
            name="condition"
            placeholder={I18n.t('ChatFlowSettings.Condition')}
          />
        </ChatFlowSection>
        <ChatFlowSection section="Fulfillment" caption>
          <MessageArray field="replies" />
          <FormControlLabel
            sx={styles.switchLabel}
            control={<Field component={Switch} size="small" name="enableIntegration" />}
            label={I18n.t('ChatFlowSettings.Enable integration')}
          />
          {enableIntegration?.length > 0 && (
            <Field
              component={FormikAutosuggest}
              suggestions={integrationSuggestions}
              placeholder={I18n.t('ChatFlowSettings.Select integration')}
              name="integration._id"
              isClearable
              clearValue={() => {
                setFieldValue('integration', {});
              }}
            />
          )}
          {enableIntegration?.length > 0 && (integration?._id || 'new') !== 'new' && (
            <Box sx={styles.intentActions}>
              <Button
                variant="text"
                color="primary"
                onClick={() => context.push({ type: 'integration', integration: integration._id })}
              >
                {I18n.t('ChatFlowSettings.Edit integration')}
              </Button>
            </Box>
          )}
        </ChatFlowSection>
        <ChatFlowSection section="Utterance parameter" caption>
          <Field
            sx={styles.field}
            component={TextField}
            name="utteranceParameter"
            placeholder={I18n.t('ChatFlowSettings.Parameter name')}
          />
        </ChatFlowSection>
        <ChatFlowSection section="Output parameters">
          <ChatFlowParametersTable name="outputParameters" />
        </ChatFlowSection>
        <ChatFlowSection section="Entity clearing" caption>
          <FormControlLabel
            sx={styles.switchLabel}
            control={<Field component={Switch} size="small" name="enableClearAllEntities" />}
            label={I18n.t('ChatFlowSettings.Clear all entities')}
          />
          {enableClearAllEntities?.length === 0 && (
            <Box sx={styles.field}>
              <Field
                component={FormikAutosuggest}
                suggestions={entitySuggestions}
                placeholder={I18n.t('ChatFlowSettings.Entity names to clear')}
                name="clearEntities"
                creatable
                isMulti
              />
            </Box>
          )}
        </ChatFlowSection>
        <ChatFlowSection section="Transition">
          <Box
            sx={{
              '& .chat-flow-radio-group': styles.horizontal,
              '& .chat-flow-radio-item': styles.optionHorizontal,
            }}
          >
            <Field
              type="radio"
              component={FormikRadioGroup}
              groupClassName="chat-flow-radio-group"
              itemClassName="chat-flow-radio-item"
              name="transitionType"
              items={['page', 'handoff'].map(i => [
                i,
                I18n.t(`ChatFlowSettings.transitionTypes.${i}`),
              ])}
            />
          </Box>
          {transitionType === 'page' && (
            <>
              <ChatFlowCaption section="Transition" />
              <Box sx={styles.field}>
                <Field
                  component={FormikAutosuggest}
                  suggestions={pageSuggestions}
                  placeholder={I18n.t('ChatFlowSettings.None')}
                  name="transition._id"
                  isClearable
                />
              </Box>
              {transition?._id === 'new' && (
                <Field
                  sx={styles.field}
                  component={TextField}
                  name="transition.name"
                  placeholder={I18n.t('ChatFlowSettings.Page name')}
                />
              )}
            </>
          )}
          {transitionType === 'handoff' && (
            <>
              <ChatFlowCaption section="TransitionHandoff" />
              <Box sx={styles.field}>
                <Field
                  component={FormikAutosuggest}
                  loadOptions={loadOptionsLazyQuery}
                  additional={{ page: 1, trigger: triggerListTeamsQuery }}
                  placeholder={I18n.t('ChatFlowSettings.None')}
                  name="handoffTo"
                  isClearable
                />
              </Box>
              <FormControlLabel
                sx={styles.switchLabel}
                control={<Field component={Switch} size="small" name="enableEmailTo" />}
                label={I18n.t('ChatFlowSettings.Notify via email')}
              />
              {enableEmailTo.length > 0 && (
                <Field
                  component={FormikChipInput}
                  blurBehavior="add"
                  placeholder={I18n.t('ChatFlowSettings.Email')}
                  name="emailTo"
                />
              )}
            </>
          )}
        </ChatFlowSection>
      </Box>
      <Box component="footer" sx={styles.footer}>
        <div />
        <Button variant="contained" color="primary" type="submit">
          {I18n.t('ChatFlowSettings.Save')}
        </Button>
      </Box>
    </Box>
  );
};

ChatFlowRoute.propTypes = {
  flow: object.isRequired,
  values: object.isRequired,
  handleSubmit: func.isRequired,
  chatFlowIntents: object.isRequired,
  context: object.isRequired,
  setFieldValue: func.isRequired,
  setSelectedIntent: func.isRequired,
  setSelectedIntegration: func.isRequired,
  teams: object,
  chatFlowIntegrations: object.isRequired,
  chatFlowEntityTypes: object,
};

export default connector(withContext(chatFlowRouteFormik(ChatFlowRoute)));
